import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAlertActions } from '@d-orca/shared/client/hooks';
import type { INotificationItem } from '@interfaces/INotificationItem';
import { selectSiteId, selectSiteTz } from '@store/selectors/site.selectors';
import { ArrivalPeek } from '../ArrivalPeek/ArrivalPeek';
import { BellMenu } from '../BellMenu/BellMenu';
import { useArrivalPeek } from '../hooks/useArrivalPeek';
import { useArrivalSound } from '../hooks/useArrivalSound';
import { useBellArrivals } from '../hooks/useBellArrivals';
import { useBellSwing } from '../hooks/useBellSwing';
import { useNotificationItems } from '../hooks/useNotificationItems';
import { useNotificationsStream } from '../hooks/useNotificationsStream';
import styles from './NotificationsBell.module.css';

/**
 * The header bell, wired: the notifications waiting for the signed-in
 * person, where picking one leads, and — when one arrives — a sound, a
 * swing of the bell, and a glimpse of it underneath.
 */
export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const siteId = useSelector(selectSiteId) ?? null;
  const tz = useSelector(selectSiteTz);
  useNotificationsStream(siteId);
  const { markAllViewed } = useAlertActions(siteId);

  const arrivals = useBellArrivals(siteId);
  useArrivalSound(arrivals.count);
  const { swinging, onSwingEnd } = useBellSwing(arrivals.count);
  const { peek, dismiss, onHoverChange } = useArrivalPeek(arrivals);

  // The peek wants names as the menu does, so it counts as the menu being open.
  const { count, items } = useNotificationItems({ open: open || peek.length > 0 });
  // The peek's rows, in the peek's order: newest arrival on top.
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const peekItems = peek.flatMap((n) => itemsById.get(n.id) ?? []);
  const openItem = (item: INotificationItem) => {
    item.onOpened?.();
    if (item.to) navigate(item.to);
    else void item.resolveTo?.().then((to) => to && navigate(to));
  };
  // Picked from the peek, it has done its job: it goes, rather than lingering over what it opened.
  const openFromPeek = (item: INotificationItem) => {
    dismiss();
    openItem(item);
  };

  return (
    <span className={styles.anchor}>
      <BellMenu
        count={count}
        items={items}
        tz={tz}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (isOpen) dismiss();
          // What the menu showed has been seen — as Trusstor's panel marks it on close.
          else if (items.some((item) => item.unread)) markAllViewed();
        }}
        onOpenItem={openItem}
        swinging={swinging}
        onSwingEnd={onSwingEnd}
      />
      {!open && peekItems.length > 0 && (
        <ArrivalPeek items={peekItems} onOpenItem={openFromPeek} onHoverChange={onHoverChange} />
      )}
    </span>
  );
}
